import './plugins/veevalidate'
import './plugins/vuetify'
import './plugins/vue-gtm'
import './plugins/one-signal'
import 'moment-duration-format'

import globalAxios, { AxiosError, AxiosRequestConfig } from 'axios'

import App from './App.vue'
import { AxiosHttpClient } from 'coloquent/dist/httpclient/axios/AxiosHttpClient'
import { Model } from 'coloquent'
import Notification from '@/classes/Notification'
import Vue from 'vue'
import i18n from './i18n'
import initializeAmplify from './plugins/amplify'
// @ts-ignore
import localization from 'moment/locale/nl'
import moment from 'moment'
import router from './router'
import { store } from '@/store'
import { v4 as uuidv4 } from 'uuid'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'
import * as Availability from '@/classes/Availability'
import Unavailable from '@/views/Unavailable.vue'

moment.locale('nl', localization)

initializeAmplify(process.env)
Vue.config.productionTip = false

const axiosClient = new AxiosHttpClient()
const modelAxios = axiosClient.getImplementingClient()

modelAxios.interceptors.request.use(addTraceId)
globalAxios.interceptors.request.use(addTraceId)
modelAxios.interceptors.request.use(addAuthHeader)
globalAxios.interceptors.request.use(addAuthHeader)
modelAxios.interceptors.response.use(trackAvailability, reportAvailabilityFailure)
globalAxios.interceptors.response.use(trackAvailability, reportAvailabilityFailure)
modelAxios.interceptors.response.use((response) => response, handleAxiosErrors)
modelAxios.interceptors.response.use((response) => response, refreshToken)
globalAxios.interceptors.response.use((response) => response, handleAxiosErrors)
globalAxios.interceptors.response.use((response) => response, refreshToken)

modelAxios.defaults.headers['Accept-Language'] = i18n.locale

async function addAuthHeader(config) {
	// skip adding headers when on the register/activate/track-and-trace route
	if (['register', 'login', 'trackandtrace'].includes(router.currentRoute?.name ?? '')) {
		return config
	}
	try {
		if (!window.localStorage.getItem('isTesting')) {
			await getCurrentUser()
			const { tokens } = await fetchAuthSession()
			config.headers.Authorization = `Bearer ${tokens?.accessToken}`
		}

		return config
	} catch (e) {
		// skip cognito "not signed in" exceptions (v5 string / v5 code / v6 AuthError name)
		if (
			e !== 'No current user' &&
			(e as any)?.code !== 'NotAuthorizedException' &&
			(e as any)?.name !== 'UserUnAuthenticatedException'
		) {
			throw e
		}
		return config
	}
}
function trackAvailability(response) {
	Availability.reportSuccess()
	return response
}
function reportAvailabilityFailure(error: AxiosError): Promise<AxiosError> {
	// the availability check must never surface as an unhandled rejection of its own
	Availability.reportFailure(error).catch(() => undefined)
	return Promise.reject(error)
}
async function addTraceId(req) {
	if (!req) return req
	req.headers['X-4S-Trace-Id'] = uuidv4()

	return req
}
async function refreshToken(error: AxiosError): Promise<any> {
	const originalRequest = error.config

	// @ts-expect-error [expects _retry to be defined on originalRequest]
	if (error.response?.status !== 401 || originalRequest?._retry) {
		return Promise.reject(error)
	}

	// @ts-ignore
	originalRequest._retry = true

	const { tokens } = await fetchAuthSession({ forceRefresh: true })
	if (tokens?.accessToken) {
		Model.getHttpClient().getImplementingClient().defaults.headers.Authorization = `Bearer ${tokens.accessToken}`
		// @ts-ignore
		originalRequest.headers = originalRequest.headers.toJSON()
		// @ts-ignore
		globalAxios.defaults.headers.Authorization = `Bearer ${tokens.accessToken}`
		// @ts-ignore
		originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`

		return globalAxios(originalRequest as AxiosRequestConfig)
	}

	await Notification.Error(vue.$t('errors.unknown').toString())
	return Promise.reject(error)
}

async function handleAxiosErrors(error: AxiosError): Promise<AxiosError> {
	if (Availability.isSuppressingErrors()) {
		// the unavailable page already says it all (BGT-8224)
		throw error
	}
	if (error?.name === 'UserUnAuthenticatedException') {
		// do nothing, we will be redirected to login page automatically
		return Promise.reject(error)
	}
	if (!error.response) {
		await Notification.Error(vue.$t('errors.generic').toString())
	} else if (error.response.status === 500) {
		await Notification.Error(vue.$t('errors.unknown').toString())
	} else if (error.response.status === 400) {
		let errorData = error.response.data as any

		if (errorData instanceof Blob) {
			errorData = JSON.parse(await errorData.text())
		}

		// Temporary fix for cognito errors; should be reformatted in backend
		if (
			Object.prototype.hasOwnProperty.call(errorData, 'data') &&
			!Object.prototype.hasOwnProperty.call(errorData, 'errors')
		) {
			errorData = errorData.data
		}

		if (errorData?.errors?.[0].length) {
			await Notification.Error(
				`<p>${i18n.t('errors.form_errors')}</p><ul><li>${errorData.errors[0]
					.map((error) => error.title)
					.join('</li><li>')}</li></ul>`
			)
		}
	} else if (error.response.status === 403) {
		await router.replace({ name: 'forbidden' })
	} else if (error.response.status === 498) {
		// TODO: handle 498 (Unauthorized from remote API like exact-online)
	}
	return Promise.reject(error)
}

Model.setHttpClient(axiosClient)

let vue: Vue

// Decide before anything renders, so an outage shows the unavailable page instead of a half-loaded app
// with an error toast (BGT-8224). During an outage only the page mounts: no router, so no route guard can
// call the API or redirect to a login it cannot serve. boot() never rejects.
Availability.boot()
	.then((state) => {
		vue = state === 'ok' ? createApp() : createUnavailablePage()
	})
	.catch((error) => {
		// eslint-disable-next-line
		console.error(error)
	})

function createUnavailablePage(): Vue {
	return new Vue({
		i18n,
		render: (h) => h('v-app', [h(Unavailable)])
	}).$mount('#app')
}

function createApp(): Vue {
	return new Vue({
		router,
		store,
		i18n,
		beforeMount() {
			if (window.localStorage.getItem('isTesting')) {
				return
			}
			if (!this.$OneSignal) {
				return
			}

			this.$OneSignal.init({
				appId: process.env.VUE_APP_ONESIGNAL_APP_ID!,
				// @ts-expect-error type issue with OneSignal SDK
				notifyButton: {
					enable: false,
					prenotify: false,
					showCredit: false
				},
				allowLocalhostAsSecureOrigin: true
			})
		},
		render: (h) => h(App)
	}).$mount('#app')
}

Vue.config.errorHandler = (error, vm, info) => {
	if (error.name?.includes('UserUnAuthenticatedException')) {
		// do nothing, we will be redirected to login page automatically
		return
	}
	// @ts-ignore
	if (window.Cypress && window.top) {
		// @ts-ignore
		window.top.console.error(error)
	}

	// eslint-disable-next-line
	console.error(error)

	if (Availability.isSuppressingErrors()) {
		return
	}

	if (error.message?.includes('ChunkLoadError')) {
		const reload = window.confirm(i18n.t('errors.chunkLoadError').toString())
		if (reload) {
			window.location.reload()
		}
	}
	Notification.Error(vue.$t('errors.generic', { error: error.message }).toString())
}

window.onresize = () => {
	store.commit('viewport/setWidth', window.innerWidth)
}
